'use client';

import React, { useEffect, useState } from 'react';
import { useBlockingRouter } from '../../hooks/useBlockingRouter';
import { getEnumKeyByValue } from '../../lib/helpers/apollo';
import { toggleLoadingOverlay } from '../../redux/actions';
import MuiStepper from '../../mui/MuiStepper';
import { YourInfo } from './YourInfo';
import { useYourInfo } from './YourInfo/useYourInfo';
import { FinishUp } from './FinishUp';
import { useFinishUp } from './FinishUp/useFinishUp';
import styles from './index.module.scss';

import {
	GuestUser,
	SuperAdmin,
	ClientUser,
	OrgUser,
	UserRegistration,
} from '../../types/user';
import { useSelector, useDispatch } from 'react-redux';
import ErrorScrollContextProvider from '../../context/ErrorScrollContextProvider';

export enum ClientOnboardingSteps {
	YourInfo = 'your-info',
	FinishUp = 'finish-up',
}

export type ClientOnboardingStepKeys = `${ ClientOnboardingSteps }`;

export type ClientOnboardingProps = {
	step: ClientOnboardingStepKeys;
};

enum Directions {
	Next = 'next',
	Back = 'back',
}

const getPath = (
	direction: Directions,
	currentStep: ClientOnboardingStepKeys
) =>
	direction === Directions.Back
		? `/clientOnboarding/${ ClientOnboardingSteps.YourInfo }`
		: currentStep === ClientOnboardingSteps.FinishUp
			? '/'
			: `/clientOnboarding/${ ClientOnboardingSteps.FinishUp }`;

const stepLabels = {
	[ ClientOnboardingSteps.YourInfo ]: 'Your info',
	[ ClientOnboardingSteps.FinishUp ]: 'Finish up',
};

type FlattenedUserForOnboarding =
	| OrgUser
	| ClientUser
	| SuperAdmin
	| GuestUser
	| undefined;

export const ClientOnboarding: React.FC<ClientOnboardingProps> = ( {
	step: stepValue,
} ) => {
	const {
		firstNameOne,
		setFirstNameOne,
		firstNameOneValidation,
		lastNameOne,
		setLastNameOne,
		lastNameOneValidation,
		firstNameTwo,
		setFirstNameTwo,
		firstNameTwoValidation,
		lastNameTwo,
		setLastNameTwo,
		lastNameTwoValidation,
		termsOfServiceAccepted,
		setTermsOfServiceAccepted,
		termsOfServiceAcceptedValidation,
		consentedToAgeAndMarketingEmails,
		setConsentedToAgeAndMarketingEmails,
		consentedToAgeAndMarketingEmailsValidation,
		onValidate: onYourInfoValidate,
		address,
		setAddress,
		addressValidation,
		onNext: onYourInfoNext,
	} = useYourInfo();
	const {
		weddingDate,
		setWeddingDate,
		weddingDateValidation,
		venue,
		setVenue,
		venueValidation,
		phone,
		setPhone,
		phoneValidation,
		onValidate,
		onNext: onFinishUpNext,
		onBack: onFinishUpBack,
	} = useFinishUp();
	const router = useBlockingRouter();
	const user: FlattenedUserForOnboarding = useSelector(
		( state: any ) => state?.user
	);
	const userRegistration: UserRegistration = useSelector(
		( state: any ) => state?.userRegistration
	);
	const [ loading, setLoading ] = useState( false );
	// if we get here from sign-in then we have to turn off the loading indicator
	const dispatch = useDispatch();
	useEffect( () => {
		dispatch( toggleLoadingOverlay( false ) );
	}, [ dispatch ] );
	useEffect( () => {
		setLoading( false );
	}, [ stepValue ] );

	const step = ClientOnboardingSteps[
		getEnumKeyByValue(
			ClientOnboardingSteps,
			stepValue
		) as keyof typeof ClientOnboardingSteps
	] as ClientOnboardingSteps;

	const activeStep = Object.values(
		ClientOnboardingSteps as Record<string, string>
	).indexOf( step );

	const handleOnNext = async () => {
		setLoading( true );
		const res =
			step === ClientOnboardingSteps.YourInfo
				? await onYourInfoNext()
				: await onFinishUpNext();
		if ( !res ) {
			setLoading( false );
			return;
		} else {
			router.push( getPath( Directions.Next, step ) );
		}
	};

	const handleOnBack = () => {
		setLoading( true );
		const res = onFinishUpBack();
		if ( !res ) {
			setLoading( false );
			return;
		}
		router.push( getPath( Directions.Back, step ) );
	};

	// If we have neither a user nor a userRegistration, then bail out and force the user to use their link again
	if ( !user?.isLoggedIn && !userRegistration?.coreUserFields ) {
		router.push( '/Login' );
	}

	if ( user?.isLoggedIn && user.user?.onboardingCompleted ) {
		router.push( '/' );
	}

	return (
		<ErrorScrollContextProvider stickyHeaderOffset>
			<main className={ styles.container }>
				<MuiStepper
					steps={ ClientOnboardingSteps }
					// more enum casting nonsense
					labels={ stepLabels }
					activeStep={ activeStep }
					onNext={ handleOnNext }
					onBack={ handleOnBack }
					loading={ loading }
				>
					{ step === ClientOnboardingSteps.YourInfo ? (
						<YourInfo
							firstNameOne={ firstNameOne }
							setFirstNameOne={ setFirstNameOne }
							firstNameOneValidation={ firstNameOneValidation }
							lastNameOne={ lastNameOne }
							setLastNameOne={ setLastNameOne }
							lastNameOneValidation={ lastNameOneValidation }
							firstNameTwo={ firstNameTwo }
							setFirstNameTwo={ setFirstNameTwo }
							firstNameTwoValidation={ firstNameTwoValidation }
							lastNameTwo={ lastNameTwo }
							setLastNameTwo={ setLastNameTwo }
							address={ address }
							setAddress={ setAddress }
							addressValidation={ addressValidation }
							lastNameTwoValidation={ lastNameTwoValidation }
							termsOfServiceAccepted={ termsOfServiceAccepted }
							setTermsOfServiceAccepted={ setTermsOfServiceAccepted }
							termsOfServiceAcceptedValidation={
								termsOfServiceAcceptedValidation
							}
							consentedToAgeAndMarketingEmails={
								consentedToAgeAndMarketingEmails
							}
							setConsentedToAgeAndMarketingEmails={
								setConsentedToAgeAndMarketingEmails
							}
							consentedToAgeAndMarketingEmailsValidation={
								consentedToAgeAndMarketingEmailsValidation
							}
							onBlur={ () => onYourInfoValidate() }
						/>
					) : null }
					{ step === ClientOnboardingSteps.FinishUp ? (
						<FinishUp
							weddingDate={ weddingDate }
							setWeddingDate={ setWeddingDate }
							weddingDateValidation={ weddingDateValidation }
							venue={ venue }
							setVenue={ setVenue }
							venueValidation={ venueValidation }
							phone={ phone }
							setPhone={ setPhone }
							phoneValidation={ phoneValidation }
							onValidate={ onValidate }
							address={ address }
						/>
					) : null }
				</MuiStepper>
			</main>
		</ErrorScrollContextProvider>
	);
};
